import { useState, useCallback, useEffect } from "react";
import {
  ChecklistCountsEntity,
  ChecklistItemEntity,
  ChecklistItemsTabStatus,
  GetChecklistCountsUseCase,
} from "../../domain";
import { ChecklistRepositoryImpl } from "../../data/repositories/ChecklistRepositoryImpl";
import { MONTH_END_CHECKLIST_ALL_AREAS_KEY } from "../../../../shared/constants/monthEndChecklist";

// Create repository instance (singleton)
const checklistRepository = new ChecklistRepositoryImpl();

// Create use case
const getChecklistCountsUseCase = new GetChecklistCountsUseCase(
  checklistRepository
);

export interface UseChecklistCountsReturn {
  counts: ChecklistCountsEntity | null;
  loading: boolean;
  error: string | null;
  fetchCounts: (month?: string) => Promise<void>;
  refresh: (month?: string) => Promise<void>;
  applyStatusTransition: (
    accountingArea: string,
    fromStatus: ChecklistItemEntity["status"],
    toStatus: ChecklistItemEntity["status"]
  ) => void;
  clearError: () => void;
}

const toCountBucket = (
  status: ChecklistItemEntity["status"]
): ChecklistItemsTabStatus => {
  if (status === "done" || status === "approved") return "done";
  if (status === "rejected") return "rejected";
  if (status === "to_be_done_later") return "to_be_done_later";
  return "pending";
};

export const useChecklistCounts = (
  month?: string
): UseChecklistCountsReturn => {
  const [counts, setCounts] = useState<ChecklistCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch checklist counts
   */
  const fetchCounts = useCallback(
    async (fetchMonth?: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const monthToUse = fetchMonth || month;
        const result = await getChecklistCountsUseCase.execute(monthToUse);
        setCounts(result);
      } catch (err: any) {
        const errorMessage = err.message || "Failed to load checklist counts";
        setError(errorMessage);
        console.error("Error fetching checklist counts:", err);

        // Clear counts data on 404 (checklist not found for this month)
        if (err.status === 404) {
          setCounts(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [month]
  );

  /**
   * Refresh counts
   */
  const refresh = useCallback(
    async (refreshMonth?: string): Promise<void> => {
      await fetchCounts(refreshMonth);
    },
    [fetchCounts]
  );

  /**
   * Update sidebar counts after a successful item status mutation.
   */
  const applyStatusTransition = useCallback(
    (
      accountingArea: string,
      fromStatus: ChecklistItemEntity["status"],
      toStatus: ChecklistItemEntity["status"]
    ): void => {
      const fromBucket = toCountBucket(fromStatus);
      const toBucket = toCountBucket(toStatus);

      if (fromBucket === toBucket) return;

      setCounts((prevCounts) => {
        if (!prevCounts || !prevCounts[accountingArea]) return prevCounts;

        const updateAreaCounts = (areaKey: string) => {
          const areaCounts = prevCounts[areaKey];

          if (!areaCounts) return areaCounts;

          return {
            ...areaCounts,
            [fromBucket]: Math.max((areaCounts[fromBucket] || 0) - 1, 0),
            [toBucket]: (areaCounts[toBucket] || 0) + 1,
          };
        };

        const nextCounts: ChecklistCountsEntity = {
          ...prevCounts,
          [accountingArea]: updateAreaCounts(accountingArea),
        };

        if (prevCounts[MONTH_END_CHECKLIST_ALL_AREAS_KEY]) {
          nextCounts[MONTH_END_CHECKLIST_ALL_AREAS_KEY] = updateAreaCounts(
            MONTH_END_CHECKLIST_ALL_AREAS_KEY
          );
        }

        return nextCounts;
      });
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset data when month changes
  useEffect(() => {
    setCounts(null);
    setError(null);
  }, [month]);

  return {
    counts,
    loading,
    error,
    fetchCounts,
    refresh,
    applyStatusTransition,
    clearError,
  };
};
